import React from "react";
import { FormInstance } from "antd";
import { MessageApi } from "antd/lib/message";
import { IDocumentsData, IProcessesData, IRegisterCheckinItem, ISaleChannelData } from "store/lobby/lobby-model";
import { IUserData } from "store/user-session/user-session-model";
import { DrawerStatusEnum } from "typings/global.types";
import { mountArrayDrivers, mountArrayOfDocuments, mountArrayOffNotEmpty, searchProcessByIdSelected, searchSaleChannelByIdSelected } from "utils/functions";
import useTranslation from "utils/hooks/useTranslation";
import { ConfirmCancelEdit } from "../lobby/components/confirm";
import { checkIfSaleChannelIsNull, mountTrailerType, validateDocumentsDuplicates } from "../lobby/lobby.service";
import * as S from "./styled/screen.styled";

export interface IVehicleAction {
  id: number;
  dock?: string;
}

type IOnConfirmVehicleAction = {
  form: FormInstance<any>;
  id?: number;
  message: MessageApi;
  messageDescription: string;
  vehicleActionSelected: number;
  attendanceSaveVehicleActionAsync: (...args) => void;
};

type IOnConfirmReleaseVehicleAction = {
  id?: number;
  attendanceSaveReleaseVehicleActionAsync: (...args) => void;
};

type IOnConfirmDrawerViewRegister = {
  visibleDrawerViewMode: DrawerStatusEnum;
  attendanceUpdateDrawerViewMode: (...args) => void;
  eventTime: string;
  form: FormInstance<any>;
  attendanceUpdateCheckinAsync: (...args) => void;
  attendanceUpdateInboundAsync: (...args) => void;
  message: MessageApi;
  messageDescription: string;
  messageErrorDuplicateDocuments: string;
  documents: Array<IDocumentsData>;
  inboundProcessesList: Array<IProcessesData>;
  userData: IUserData;
  processActive: number;
  saleChannelActive: number;
  salesChannelList: Array<ISaleChannelData>;
  checkinRegisterActive?: IRegisterCheckinItem;
};

type IOnConfirmCancelEdit = {
  handleOnConfirm: () => void;
  okText: string;
  cancelText: string;
  visibleDrawerViewMode: DrawerStatusEnum;
  formViewOrEditModified: boolean;
  attendanceHideDrawerViewRegister: () => void;
  disabled: boolean;
};

type IOnConfirmDrawerCheckinRegister = {
  form: FormInstance<any>;
  userData: IUserData;
  eventTime: string;
  message: MessageApi;
  documents: Array<IDocumentsData>;
  processActive: number;
  saleChannelActive: number;
  salesChannelList: Array<ISaleChannelData>;
  messageDescription: string;
  inboundProcessesList: Array<IProcessesData>;
  checkinRegisterActive?: IRegisterCheckinItem;
  messageErrorDuplicateDocuments: string;
  attendanceSaveCheckinRegisterAsync: (...args) => void;
};

type IOnConfirmExcludeCheckinRegister = {
  checkinRegisterActive?: IRegisterCheckinItem;
  message: MessageApi;
  messageDescription: string;
  attendanceRemoveCheckinRegisterAsync: (...args) => void;
};

export const onConfirmVehicleAction = async ({ form, id, message, messageDescription, vehicleActionSelected, attendanceSaveVehicleActionAsync }: IOnConfirmVehicleAction) => {
  try {
    const formaData = await form.validateFields();
    const { dockAttendance } = formaData;
    const optionDockInboundSelected = 2;
    let bodyRequest: IVehicleAction = { id: id };
    if (vehicleActionSelected === optionDockInboundSelected) {
      bodyRequest = { ...bodyRequest, dock: dockAttendance };
    }
    attendanceSaveVehicleActionAsync(bodyRequest);
  } catch (e) {
    console.error(new Error("Vehicle action error"));
    message.error(messageDescription);
  }
};

export const onConfirmReleaseVehicleAction = async ({ id, attendanceSaveReleaseVehicleActionAsync }: IOnConfirmReleaseVehicleAction) => {
  try {
    attendanceSaveReleaseVehicleActionAsync({ id: id });
  } catch (e) {
    console.error(new Error("Release vehicle action error"));
  }
};

export const onConfirmDrawerViewRegister = async ({
  visibleDrawerViewMode,
  attendanceUpdateDrawerViewMode,
  eventTime,
  form,
  attendanceUpdateCheckinAsync,
  message,
  messageDescription,
  messageErrorDuplicateDocuments,
  documents,
  inboundProcessesList,
  userData,
  processActive,
  saleChannelActive,
  salesChannelList,
  checkinRegisterActive,
  attendanceUpdateInboundAsync,
}: IOnConfirmDrawerViewRegister) => {
  if (visibleDrawerViewMode === DrawerStatusEnum.View) {
    attendanceUpdateDrawerViewMode(DrawerStatusEnum.Edit);
    return;
  }
  try {
    const formaData = await form.validateFields();
    const { carrier, vehiclePlate, trailersPlates, observation, processDescription, cpf1, driver1, trailerType } = formaData;
    const process = !isNaN(processDescription) === true ? searchProcessByIdSelected(processDescription, inboundProcessesList) : processDescription;
    const saleChannelDescription = searchSaleChannelByIdSelected(saleChannelActive, salesChannelList);
    const documentFormated = mountArrayOfDocuments(documents, formaData);
    if (!validateDocumentsDuplicates(documentFormated)) {
      const bodyRequest = {
        carrier,
        vehiclePlate: vehiclePlate.toUpperCase(),
        trailersPlates: mountArrayOffNotEmpty(trailersPlates),
        observation,
        user: null,
        eventTime,
        locationId: +userData?.locationId,
        processId: processActive,
        processDescription: process,
        saleChannel: checkIfSaleChannelIsNull(saleChannelActive, saleChannelDescription),
        drivers: mountArrayDrivers(cpf1, driver1),
        documents: documentFormated,
        ...mountTrailerType(trailerType),
      };
      if (checkinRegisterActive?.isCheckin) {
        attendanceUpdateCheckinAsync(bodyRequest);
      } else {
        attendanceUpdateInboundAsync(bodyRequest);
      }
    } else {
      message.error(messageErrorDuplicateDocuments);
    }
  } catch (e) {
    console.error(new Error("Edit form validation error"));
    message.error(messageDescription);
  }
};

const isConfirmModalOpen = (visibleDrawerViewMode, formViewOrEditModified) => visibleDrawerViewMode === DrawerStatusEnum.Edit && formViewOrEditModified === true;

export const OnConfirmCancelEdit = ({
  handleOnConfirm,
  okText,
  cancelText,
  visibleDrawerViewMode,
  formViewOrEditModified,
  disabled,
  attendanceHideDrawerViewRegister,
}: IOnConfirmCancelEdit) => {
  const CANCEL = useTranslation({ id: "cancel" });
  return isConfirmModalOpen(visibleDrawerViewMode, formViewOrEditModified) ? (
    ConfirmCancelEdit({
      handleOnConfirm: handleOnConfirm,
      okText: okText,
      cancelText: cancelText,
      disabled: disabled,
    })
  ) : (
    <S.CancelButton htmlType="submit" onClick={attendanceHideDrawerViewRegister} disabled={disabled}>
      {CANCEL}
    </S.CancelButton>
  );
};

export const onConfirmDrawerCheckinRegister = async ({
  form,
  userData,
  eventTime,
  message,
  documents,
  processActive,
  saleChannelActive,
  salesChannelList,
  messageDescription,
  inboundProcessesList,
  checkinRegisterActive,
  messageErrorDuplicateDocuments,
  attendanceSaveCheckinRegisterAsync,
}: IOnConfirmDrawerCheckinRegister) => {
  try {
    const formaData = await form.validateFields();
    const { carrier, vehiclePlate, trailersPlates, processDescription, observation, cpf1, driver1, trailerType } = formaData;
    const process = !isNaN(processDescription) === true ? searchProcessByIdSelected(processDescription, inboundProcessesList) : processDescription;
    const saleChannelDescription = searchSaleChannelByIdSelected(saleChannelActive, salesChannelList);
    const documentFormated = mountArrayOfDocuments(documents, formaData);
    if (!validateDocumentsDuplicates(documentFormated)) {
      const bodyRequest = {
        carrier,
        vehiclePlate: vehiclePlate.toUpperCase(),
        trailersPlates: mountArrayOffNotEmpty(trailersPlates),
        drivers: mountArrayDrivers(cpf1, driver1),
        documents: documentFormated,
        saleChannel: checkIfSaleChannelIsNull(saleChannelActive, saleChannelDescription),
        eventTime,
        user: null,
        locationId: +userData?.locationId,
        processId: processActive,
        processDescription: process,
        observation,
        yardRegisterId: checkinRegisterActive?.yardRegisterId,
        ...mountTrailerType(trailerType),
      };
      attendanceSaveCheckinRegisterAsync(bodyRequest);
    } else {
      message.error(messageErrorDuplicateDocuments);
    }
  } catch (e) {
    console.error(new Error("Checkin form validation error"));
    message.error(messageDescription);
  }
};

export const onConfirmExcludeCheckinRegister = async ({
  checkinRegisterActive,
  message,
  messageDescription,
  attendanceRemoveCheckinRegisterAsync,
}: IOnConfirmExcludeCheckinRegister) => {
  try {
    const bodyRequest = {
      yardRegisterId: checkinRegisterActive?.yardRegisterId,
    };
    attendanceRemoveCheckinRegisterAsync(bodyRequest);
  } catch (e) {
    console.error(new Error("Checkin form validation error"));
    message.error(messageDescription);
  }
};
